import { NgClass } from '@angular/common';
import { Component } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatInput, MatSuffix } from '@angular/material/input';
import { Router } from '@angular/router';
import { AuthInterceptorService } from '../../../services/interceptors/auth-interceptor.service';
import { CustomKeycloakService } from '../../../services/keycloak.service';

@Component({
  selector: 'frmg-login-dialog',
  templateUrl: './login-dialog.component.html',
  styleUrls: ['./login-dialog.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatInput,
    MatSuffix,
    NgClass,
    MatButton
  ]
})
export class LoginDialogComponent {
  public loginFormGroup: FormGroup;
  public resetFormGroup: FormGroup;
  public hide: boolean = true;
  public loginError: boolean = false;
  public state: string = 'login';
  public resetSuccess: boolean = false;

  constructor(
    private authInterceptorService: AuthInterceptorService,
    private keycloakService: CustomKeycloakService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.loginFormGroup = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]]
    });
  }

  togglePassword(event: MouseEvent) {
    this.hide = !this.hide;
    event.stopPropagation();
  }

  async login() {
    if (this.loginFormGroup.valid) {
      const value = this.loginFormGroup.value;
      this.loginError = false;

      try {
        const tokenResponse = await this.keycloakService.loginWithCredentials(
          value.email,
          value.password
        );

        if (tokenResponse.access_token) {
          localStorage.setItem(
            'keycloak_access_token',
            tokenResponse.access_token
          );
          localStorage.setItem(
            'keycloak_refresh_token',
            tokenResponse.refresh_token || ''
          );
          localStorage.setItem('keycloak_token_type', tokenResponse.token_type);
          localStorage.setItem(
            'keycloak_expires_in',
            tokenResponse.expires_in.toString()
          );
          localStorage.setItem('keycloak_scope', tokenResponse.scope);

          const expirationTime = Date.now() + tokenResponse.expires_in * 1000;
          localStorage.setItem(
            'keycloak_token_expires',
            expirationTime.toString()
          );

          localStorage.removeItem('user');
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');

          try {
            const userInfo = await this.fetchKeycloakUserInfo(tokenResponse.access_token);
            if (userInfo) {
              const formattedUser = this.formatKeycloakUserForLegacy(userInfo);
              localStorage.setItem('user', JSON.stringify(formattedUser));
            }
          } catch (error) {
            console.warn('Failed to fetch user info immediately after login:', error);
          }
        }

        this.router.navigate(['/']);
      } catch (keycloakError) {
        console.error('Keycloak login failed, trying fallback:', keycloakError);

        try {
          this.authInterceptorService
            .getTokens(value.email, value.password)
            .subscribe(
              () => {
                console.log('Legacy authentication successful');
                this.router.navigate(['/']);
              },
              (legacyError) => {
                console.error(
                  'Both Keycloak and legacy authentication failed:',
                  legacyError
                );
                this.loginError = true;
              }
            );
        } catch (fallbackError) {
          console.error('Fallback authentication error:', fallbackError);
          this.loginError = true;
        }
      }
    }
  }

  hideError() {
    this.loginError = false;
  }

  forgot() {
    this.resetFormGroup = this.fb.group({
      email: ['', [Validators.required, Validators.email]]
    });
    this.state = 'reset';
  }

  reset() {
    this.authInterceptorService
      .resetPassword(this.resetFormGroup.value.email)
      .subscribe(
        (_re) => {
          this.resetSuccess = true;
        },
        (_error) => {
          this.loginError = true;
        }
      );
  }

  backToLogin() {
    this.state = 'login';
    this.resetSuccess = false;
  }

  resetSubHeaderSuccess() {
    let email = this.resetFormGroup.value.email;
    email = email.split('');
    const finalArr = [];
    const len = email.indexOf('@');
    email.forEach((item, pos) => {
      pos >= 1 && pos <= len - 2
        ? finalArr.push('*')
        : finalArr.push(email[pos]);
    });
    return `We have sent an email with password reset information to ${finalArr.join(
      ''
    )}.`;
  }

  /**
   * Fetch user info from Keycloak
   */
  private async fetchKeycloakUserInfo(accessToken: string): Promise<any> {
    try {
      const userInfoUrl = `${this.keycloakService.getKeycloakUrl()}/realms/${this.keycloakService.getKeycloakRealm()}/protocol/openid-connect/userinfo`;

      const response = await fetch(userInfoUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json'
        }
      });

      if (response.ok) {
        return await response.json();
      } else {
        console.warn('Failed to fetch Keycloak user info:', response.status);
        return null;
      }
    } catch (error) {
      console.warn('Error fetching Keycloak user info:', error);
      return null;
    }
  }

  private formatKeycloakUserForLegacy(keycloakUser: any): any {
    return {
      id: keycloakUser.sub,
      email: keycloakUser.email,
      username: keycloakUser.preferred_username || keycloakUser.email,
      first_name: keycloakUser.given_name || '',
      last_name: keycloakUser.family_name || '',
      is_active: true,
      is_verified: keycloakUser.email_verified || false,
      teams: [
        {
          id: 1,
          name: 'ninjas',
          slug: 'ninjas',
          role: 'member'
        }
      ],
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      _keycloak_user: true,
      _keycloak_data: keycloakUser
    };
  }
}
